import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const RefundPolicyPage = () => {
  const navigate = useNavigate();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO 
        title="Refund Policy"
        description="Read the Refund Policy for AMP Predict subscriptions. Understand eligibility, refund calculations, and the process for requesting refunds."
        keywords="refund policy, subscription refund, AMP Predict refund, cancellation policy"
        url="https://amppredict.com/refund-policy"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#F97316]/20 rounded-xl">
            <RefreshCcw className="h-6 w-6 text-[#F97316]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Refund Policy</h1>
            <p className="text-gray-400 text-sm">Effective Date: 01/31/2026</p>
          </div>
        </div>

        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-2">
              <strong>Company:</strong> ANJO LC
            </p>
            <p className="text-gray-300 mb-6">
              <strong>Contact:</strong> <a href="mailto:amp@anjolc.com" className="text-[#3B82F6] hover:underline">amp@anjolc.com</a>
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">1) Overview</h2>
              <p className="text-gray-300">
                ANJO LC provides subscription-based services. This Refund Policy explains when refund requests may be submitted and how refunds are handled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">2) Admin Approval Required</h2>
              <p className="text-gray-300">
                All refund requests are subject to review and approval. Submitting a refund request does not guarantee approval.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">3) Eligibility Window</h2>
              <p className="text-gray-300">
                You may submit a prorated refund request within <strong>7 days</strong> of a renewal charge (the date the payment was successfully processed).
              </p>
              <p className="text-gray-300 mt-2">
                Requests submitted after the 7-day window may be reviewed on a case-by-case basis, but are not guaranteed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">4) Prorated Refunds (How They Are Calculated)</h2>
              <p className="text-gray-300 mb-4">
                When approved, refunds may be prorated based on the time remaining in the current billing period.
              </p>
              <p className="text-gray-300 mb-2">Proration is generally calculated using:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>The current billing period start and end dates, and</li>
                <li>The percentage of time remaining at the time the request is submitted.</li>
              </ul>
              <p className="text-gray-300 mt-4">
                Proration is an estimate and may be adjusted based on billing records, service delivery, and any prior credits or refunds.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">5) Minimum Refund Threshold</h2>
              <p className="text-gray-300">
                Refunds are not issued where the remaining prorated refund amount is under <strong>$5.00</strong> (or local equivalent). This avoids processing costs exceeding the refund value.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">6) Non-Refundable Situations</h2>
              <p className="text-gray-300 mb-2">Refunds are generally not available in the following circumstances:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>The subscription period has ended</li>
                <li>The charge has already been fully refunded</li>
                <li>The request is submitted outside the eligibility window (unless approved as an exception)</li>
                <li>Where required information is missing or the payment cannot be verified</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">7) Duplicate Charges and Billing Errors</h2>
              <p className="text-gray-300">
                If you believe you were charged incorrectly (duplicate charge, incorrect plan, billing error), contact us at <a href="mailto:amp@anjolc.com" className="text-[#3B82F6] hover:underline">amp@anjolc.com</a>. Verified billing errors may be refunded or corrected, including outside the normal window when appropriate.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">8) How Refunds Are Issued</h2>
              <p className="text-gray-300">
                Approved refunds are issued to the original payment method used at checkout. We do not issue refunds to alternative cards or bank accounts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">9) Refund Timing</h2>
              <p className="text-gray-300">
                Once a refund is approved and processed, it typically takes <strong>5–10 business days</strong> for the funds to appear, depending on your bank or card issuer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">10) How to Request a Refund</h2>
              <p className="text-gray-300 mb-2">To request a prorated refund:</p>
              <ol className="list-decimal list-inside text-gray-300 space-y-1">
                <li>Sign in to your account</li>
                <li>Go to <strong>Profile → Billing</strong></li>
                <li>Select the invoice and submit a <strong>Refund Request</strong></li>
                <li>You will receive a status update after review</li>
              </ol>
              <p className="text-gray-300 mt-4">
                If you cannot access your account, email <a href="mailto:amp@anjolc.com" className="text-[#3B82F6] hover:underline">amp@anjolc.com</a> with your organization name and the invoice date/amount.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11) Changes to This Policy</h2>
              <p className="text-gray-300">
                ANJO LC may update this Refund Policy from time to time. The most current version will be posted on this page with an updated effective date.
              </p>
            </section>
          </div>
        </div>

        {/* CTA to Billing */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/profile/billing')}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
          >
            Go to Billing Page
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
